import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNonInitialEffect } from '@versiondos/hooks';
import { Belonging } from '@spike/belonging-model';
import {
    BelongingsStatus,
    createBelongingThunk,
    deleteBelongingThunk,
    fetchBelongingsThunk,
    updateBelongingThunk
} from '@spike/belongings-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { TextField2 } from 'components/UI';
import Button from 'components/UI/V2/Button/Button';
import BusinessSettingsTitle from '../UI/BusinessSettingsTitle';
import BelongingsTable from './BelongingsTable';
import DeleteBelongingModal from './DeleteBelongingModal';
import styles from './Belongings.module.scss';

export interface BelongingsProps {
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

const DUPLICATE_ERROR = 'This belonging is already added';

export const Belongings: React.FC<BelongingsProps> = ({ className, openSidebar, isMobile }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const belongings = useSelector<RootState, Array<Belonging>>(state => state.belongings.belongings);
    const loading = useSelector<RootState, boolean>(state => state.belongings.loading);
    const status = useSelector<RootState, BelongingsStatus>(state => state.belongings.status);
    const validationErrors = useSelector<RootState, { name: string[] } | undefined>(
        state => state.belongings.validationErrors
    );

    const [searchText, setSearchText] = useState('');
    const [newBelongingName, setNewBelongingName] = useState('');
    const [editingBelonging, setEditingBelonging] = useState<Belonging | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [belongingToDelete, setBelongingToDelete] = useState<Belonging | null>(null);
    const [duplicateError, setDuplicateError] = useState('');

    useEffect(() => {
        if (belongings.length === 0) {
            dispatch(fetchBelongingsThunk(apiClientWrapper));
        }
        // apiClientWrapper is recreated each render; fetch only on mount when store is empty
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        if (
            status === BelongingsStatus.CreateSuccess ||
            status === BelongingsStatus.UpdateSuccess ||
            status === BelongingsStatus.DeleteSuccess
        ) {
            setNewBelongingName('');
            setShowForm(false);
            setShowModalDelete(false);
            setEditingBelonging(null);
            setBelongingToDelete(null);
            setDuplicateError('');
        }
    }, [status]);

    useEffect(() => {
        if (status === BelongingsStatus.ValidationError && validationErrors?.name?.length) {
            setDuplicateError(validationErrors.name[0]);
        }
    }, [status, validationErrors]);

    const filteredBelongings = belongings
        .filter(belonging => belonging.name.toLowerCase().includes(searchText.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

    const isDuplicate = (name: string, editing: Belonging | null): boolean => {
        const normalizedName = name.trim().toLowerCase();
        return belongings.some(
            belonging => belonging.name.trim().toLowerCase() === normalizedName && belonging.id !== editing?.id
        );
    };

    const handleAddNew = () => {
        setDuplicateError('');
        setEditingBelonging(null);
        setNewBelongingName('');
        setShowForm(true);
    };

    const handleEdit = (belonging: Belonging) => {
        setNewBelongingName(belonging.name);
        setEditingBelonging(belonging);
        setDuplicateError('');
        setShowForm(true);
    };

    const handleOpenModalDelete = (belonging: Belonging) => {
        setBelongingToDelete(belonging);
        setShowModalDelete(true);
    };

    const handleCancel = () => {
        setShowForm(false);
        setNewBelongingName('');
        setEditingBelonging(null);
        setDuplicateError('');
    };

    const handleCancelModal = () => {
        setShowModalDelete(false);
        setBelongingToDelete(null);
    };

    const handleDeleteBelonging = () => {
        if (!belongingToDelete) {
            return;
        }

        dispatch(deleteBelongingThunk(apiClientWrapper, belongingToDelete.id));
    };

    const handleSaveBelonging = () => {
        const trimmedName = newBelongingName.trim();

        if (trimmedName === '') {
            return;
        }

        if (isDuplicate(trimmedName, editingBelonging)) {
            setDuplicateError(DUPLICATE_ERROR);
            return;
        }

        setDuplicateError('');

        if (editingBelonging) {
            dispatch(updateBelongingThunk(apiClientWrapper, editingBelonging.id, trimmedName));
            return;
        }

        dispatch(createBelongingThunk(apiClientWrapper, trimmedName));
    };

    const handleNameChange = (name: string) => {
        setNewBelongingName(name);
        if (duplicateError) {
            setDuplicateError('');
        }
    };

    const searchIcon = (
        <FontAwesomeIcon
            icon={faSearch}
            className={styles.searchIcon}
        />
    );

    return (
        <div className={clsx(styles.container, className)}>
            <div className={styles.titleContainer}>
                <BusinessSettingsTitle className={styles.businessSettingsTitle}>
                    {isMobile && (
                        <FontAwesomeIcon
                            className={styles.iconBusinessTitle}
                            onClick={openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}
                    Belongings
                </BusinessSettingsTitle>
                <p className={styles.description}>
                    Add belongings to keep track of each pet&apos;s favorite items from home.
                </p>
            </div>

            <TextField2
                id="belongings_search_by_name_input"
                placeholder="Search by Belonging Name"
                className={styles.searchInput}
                value={searchText}
                onChange={setSearchText}
                startIcon={searchIcon}
            />

            <BelongingsTable
                belongings={filteredBelongings}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleOpenModalDelete}
            />

            {showForm ? (
                <div className={styles.formWrapper}>
                    <div className={styles.formContainer}>
                        <TextField2
                            id="business_settings_belongings_input_name"
                            className={styles.nameField}
                            name="name"
                            placeholder="Belonging Name"
                            value={newBelongingName}
                            onChange={handleNameChange}
                            required={true}
                        />
                        <Button
                            label={editingBelonging ? 'Update' : 'Save'}
                            variant="green"
                            size="medium"
                            onClick={handleSaveBelonging}
                            className={styles.saveButton}
                        />
                        <FontAwesomeIcon
                            icon={faXmark}
                            className={clsx(styles.cancelIcon, loading && styles.disabled)}
                            onClick={handleCancel}
                        />
                    </div>
                    {duplicateError && <p className={styles.errorMessage}>{duplicateError}</p>}
                </div>
            ) : (
                <div className={styles.addNewContainer}>
                    <p
                        className={clsx(styles.addNew, loading && styles.disabled)}
                        onClick={() => !loading && handleAddNew()}
                    >
                        + Add New
                    </p>
                </div>
            )}

            {showModalDelete && belongingToDelete && (
                <DeleteBelongingModal
                    belongingName={belongingToDelete.name}
                    isLoading={loading}
                    onConfirm={handleDeleteBelonging}
                    onCancel={handleCancelModal}
                />
            )}
        </div>
    );
};

export default Belongings;
